import { objectWithoutProperties as _objectWithoutProperties, extends as _extends, objectSpread2 as _objectSpread2 } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import propTypes from '../../../node_modules/prop-types/index.js';
import React, { useMemo, useCallback } from 'react';
import classnames from '../../../node_modules/classnames/index.js';
import { styled, useThemeProps, Dialog, DialogContent, Paper, DialogTitle, DialogActions } from '@mui/material';
import ModalContext from '../ModalContext.js';
import Loadable from '../../data/Loadable.js';
import CloseIconButton from '../buttons/CloseIconButton.js';
import CancelButton from '../buttons/CancelButton.js';
import SubmitButton from '../buttons/SubmitButton.js';
import { createComponentSlot } from '../../../modules/styling/theme.js';

var _excluded = ["draggableComponent", "draggableProps"],
    _excluded2 = ["header", "closeIcon", "actions", "loading", "submitting", "slowLoading", "slowSubmitting", "error", "submitLabel", "submitIcon", "cancelLabel", "cancelIcon", "submitDisabled", "cancelDisabled", "onSubmit", "onCancel", "size", "invertLayout", "allowClickPropagation", "allowKeyPropagation", "allowFocusPropagation", "draggableComponent", "draggableProps", "PaperComponent", "PaperProps", "className", "sx", "variant", "children"];
var componentRoot = createComponentSlot({
  name: "CinModal",
  slot: "Root"
});

var TwoButtonModalPaper = function TwoButtonModalPaper(_ref) {
  var DraggableComponent = _ref.draggableComponent,
      draggableProps = _ref.draggableProps,
      paperProps = _objectWithoutProperties(_ref, _excluded);

  return DraggableComponent ? /*#__PURE__*/React.createElement(DraggableComponent, draggableProps, /*#__PURE__*/React.createElement(Paper, paperProps)) : /*#__PURE__*/React.createElement(Paper, paperProps);
};

TwoButtonModalPaper.propTypes = {
  draggableComponent: propTypes.any,
  draggableProps: propTypes.any
};

var TwoButtonModalActions = function TwoButtonModalActions() {
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(CancelButton, null), /*#__PURE__*/React.createElement(SubmitButton, null));
};

var TwoButtonModalBase = function TwoButtonModalBase(_ref2) {
  var _ref2$header = _ref2.header,
      header = _ref2$header === void 0 ? null : _ref2$header,
      closeIcon = _ref2.closeIcon,
      _ref2$actions = _ref2.actions,
      actions = _ref2$actions === void 0 ? /*#__PURE__*/React.createElement(TwoButtonModalActions, null) : _ref2$actions,
      loading = _ref2.loading,
      submitting = _ref2.submitting,
      slowLoading = _ref2.slowLoading,
      slowSubmitting = _ref2.slowSubmitting,
      error = _ref2.error,
      _ref2$submitLabel = _ref2.submitLabel,
      submitLabel = _ref2$submitLabel === void 0 ? "Submit" : _ref2$submitLabel,
      submitIcon = _ref2.submitIcon,
      _ref2$cancelLabel = _ref2.cancelLabel,
      cancelLabel = _ref2$cancelLabel === void 0 ? "Cancel" : _ref2$cancelLabel,
      cancelIcon = _ref2.cancelIcon,
      submitDisabled = _ref2.submitDisabled,
      cancelDisabled = _ref2.cancelDisabled,
      onSubmit = _ref2.onSubmit,
      onCancel = _ref2.onCancel,
      size = _ref2.size,
      invertLayout = _ref2.invertLayout,
      _ref2$allowClickPropa = _ref2.allowClickPropagation,
      allowClickPropagation = _ref2$allowClickPropa === void 0 ? false : _ref2$allowClickPropa,
      _ref2$allowKeyPropaga = _ref2.allowKeyPropagation,
      allowKeyPropagation = _ref2$allowKeyPropaga === void 0 ? false : _ref2$allowKeyPropaga,
      _ref2$allowFocusPropa = _ref2.allowFocusPropagation,
      allowFocusPropagation = _ref2$allowFocusPropa === void 0 ? false : _ref2$allowFocusPropa,
      draggableComponent = _ref2.draggableComponent,
      draggableProps = _ref2.draggableProps;
      _ref2.PaperComponent;
      var PaperProps = _ref2.PaperProps,
      className = _ref2.className;
      _ref2.sx;
      _ref2.variant;
      var children = _ref2.children,
      otherProps = _objectWithoutProperties(_ref2, _excluded2);

  var modalContext = useMemo(function () {
    return {
      onCancel: onCancel,
      cancelLabel: cancelLabel,
      cancelIcon: cancelIcon,
      cancelDisabled: cancelDisabled,
      onSubmit: onSubmit,
      submitLabel: submitLabel,
      submitIcon: submitIcon,
      submitDisabled: submitDisabled,
      loading: loading,
      slowLoading: slowLoading,
      submitting: submitting,
      slowSubmitting: slowSubmitting,
      closeIcon: closeIcon
    };
  }, [onCancel, cancelLabel, cancelIcon, cancelDisabled, onSubmit, submitLabel, submitIcon, submitDisabled, loading, slowLoading, submitting, slowSubmitting, closeIcon]);
  var onStopPropagation = useCallback(function (e) {
    e.stopPropagation();
  }, []);
  var headerContent = header !== null ? /*#__PURE__*/React.createElement(DialogTitle, null, header, /*#__PURE__*/React.createElement(CloseIconButton, null)) : null;
  var actionsContent = actions !== null ? /*#__PURE__*/React.createElement(DialogActions, null, actions) : null;
  return /*#__PURE__*/React.createElement(Dialog, _extends({
    fullWidth: !!size,
    maxWidth: size,
    onClick: allowClickPropagation ? undefined : onStopPropagation,
    onMouseDown: allowClickPropagation ? undefined : onStopPropagation,
    onMouseUp: allowClickPropagation ? undefined : onStopPropagation,
    onFocus: allowFocusPropagation ? undefined : onStopPropagation,
    onBlur: allowFocusPropagation ? undefined : onStopPropagation,
    onKeyDown: allowKeyPropagation ? undefined : onStopPropagation,
    onKeyUp: allowKeyPropagation ? undefined : onStopPropagation
  }, otherProps, {
    className: classnames(componentRoot.className, className),
    PaperComponent: TwoButtonModalPaper,
    PaperProps: _objectSpread2(_objectSpread2({}, PaperProps), {}, {
      draggableComponent: draggableComponent,
      draggableProps: draggableProps
    })
  }), /*#__PURE__*/React.createElement(ModalContext.Provider, {
    value: modalContext
  }, invertLayout ? actionsContent : headerContent, /*#__PURE__*/React.createElement(DialogContent, {
    dividers: true
  }, /*#__PURE__*/React.createElement(Loadable, {
    loading: loading,
    slowLoading: slowLoading,
    error: error
  }, children)), invertLayout ? headerContent : actionsContent));
};

TwoButtonModalBase.propTypes = {
  header: propTypes.node,
  closeIcon: propTypes.node,
  actions: propTypes.node,
  loading: propTypes.bool,
  submitting: propTypes.bool,
  slowLoading: propTypes.bool,
  slowSubmitting: propTypes.bool,
  error: propTypes.any,
  submitLabel: propTypes.node,
  submitIcon: propTypes.node,
  cancelLabel: propTypes.node,
  cancelIcon: propTypes.node,
  submitDisabled: propTypes.bool,
  cancelDisabled: propTypes.bool,
  onSubmit: propTypes.func,
  onCancel: propTypes.func,
  size: propTypes.any,
  invertLayout: propTypes.bool,
  allowClickPropagation: propTypes.bool,
  allowKeyPropagation: propTypes.bool,
  allowFocusPropagation: propTypes.bool,
  draggableComponent: propTypes.elementType,
  draggableProps: propTypes.any,
  className: propTypes.string,
  sx: propTypes.any,
  variant: propTypes.any
};
var TwoButtonModalRoot = styled(TwoButtonModalBase, componentRoot.styledOptions)(function (_ref3) {
  var theme = _ref3.theme;
  return {
    ".MuiDialogTitle-root": {
      position: "relative"
    },
    ".MuiDialogContent-root": {
      padding: 0,
      "> .CinLoadable-root": {
        padding: theme.spacing(2, 3)
      }
    },
    ".MuiDialogActions-root": {
      padding: theme.spacing(2, 3)
    }
  };
});

var TwoButtonModal = function TwoButtonModal(props) {
  var rootProps = useThemeProps({
    props: props,
    name: componentRoot.name
  });
  return /*#__PURE__*/React.createElement(TwoButtonModalRoot, rootProps);
};

var TwoButtonModal$1 = TwoButtonModal;

export { TwoButtonModal$1 as default };
